import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ArrowLeft, Trash2, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DeleteData() {
  const { user, isAuthenticated } = useAuth();
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-network-hub/artifacts/d66f2cg2_IMG_0019.png";

  const handleSubmit = async () => {
    if (!confirmed) return toast.error('Please confirm you understand this action');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/users/delete-account`, { reason }, { withCredentials: true });
      setSubmitted(true);
      toast.success('Deletion request submitted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-muted text-foreground" data-testid="delete-data-page">
      <header className="border-b border-border bg-card/80 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="FendanaCura" className="h-8 w-auto" />
            <span className="text-xl font-heading font-bold text-[#00A6A0]">FendanaCura</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-muted-foreground">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 rounded-xl bg-red-500/10">
            <Trash2 className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-bold">Delete Your Data</h1>
            <p className="text-muted-foreground text-sm">Request permanent account and data deletion</p>
          </div>
        </div>

        {submitted ? (
          <Card className="bg-card border-emerald-500/20">
            <CardContent className="p-8 text-center">
              <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
              <h2 className="text-xl font-heading font-bold text-foreground mb-2">Request Submitted</h2>
              <p className="text-muted-foreground">
                Your account deletion request has been received. Your data will be permanently deleted within 30 days.
                You will receive an email confirmation once the process is complete.
              </p>
              <Link to="/" className="inline-block mt-6">
                <Button className="btn-primary" data-testid="back-home-btn">Return to Home</Button>
              </Link>
            </CardContent>
          </Card>
        ) : !isAuthenticated ? (
          <Card className="glass-card border-border">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h2 className="text-lg font-heading text-foreground mb-2">Authentication Required</h2>
              <p className="text-muted-foreground mb-4">You must be logged in to request data deletion.</p>
              <Link to="/login">
                <Button className="btn-primary" data-testid="login-to-delete-btn">Sign In</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card className="bg-red-500/5 border-red-500/20">
              <CardContent className="p-6">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-red-400 mb-1">Warning: This action is irreversible</h3>
                    <p className="text-xs text-muted-foreground">
                      Deleting your account will permanently remove all your personal data, including profile information,
                      messages, care journals, shift history, documents, and any other data associated with your account.
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-lg text-foreground">What will be deleted</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  'Your profile information and photo',
                  'All messages and conversations',
                  'Shift history and timesheets',
                  'Care journals and incident reports',
                  'Documents and uploaded files',
                  'Notification preferences and settings',
                  'Login credentials and authentication data'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Trash2 className="w-3.5 h-3.5 text-red-400/60" />
                    {item}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-lg text-foreground">Deletion Request</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Account: <span className="text-[#00A6A0]">{user?.email}</span></p>
                  <p className="text-sm text-muted-foreground">Role: <span className="text-[#FF6F61]">{user?.role}</span></p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Reason for leaving (optional)</label>
                  <textarea
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="Help us understand why you're leaving..."
                    className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground min-h-[80px] resize-none"
                    data-testid="deletion-reason-input"
                  />
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={e => setConfirmed(e.target.checked)}
                    className="mt-1 rounded border-border bg-muted"
                    data-testid="confirm-deletion-checkbox"
                  />
                  <span className="text-sm text-muted-foreground">
                    I understand that this action is permanent and all my data will be deleted within 30 days. This cannot be reversed.
                  </span>
                </label>
                <Button
                  onClick={handleSubmit}
                  disabled={!confirmed || loading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  data-testid="submit-deletion-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  {loading ? 'Submitting...' : 'Delete My Account & Data'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      <footer className="border-t border-border py-8 px-4 text-center">
        <div className="max-w-2xl mx-auto flex items-center justify-center gap-6 text-sm text-muted-foreground">
          <Link to="/privacy" className="hover:text-muted-foreground">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-muted-foreground">Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
}
